import React, { useState, useEffect, useCallback } from 'react';
import { hot } from 'react-hot-loader';
import css from './App.css';
import StyledButton from '../common/StyledButton';
import Modal from "react-modal";
import Footer from '../common/Footer';
import Header from '../common/Header';
import Icon from 'SF/MaterialIcon';
import { uniq, isEqual } from "lodash";
import CalendarTable from './CalendarTable';
import Toast from 'common/Toast';
import useStarfishAPI from 'hooks/useStarfishAPI';
import ConfirmDialog from '../common/ConfirmDialog';

const App = () => {
  Modal.setAppElement(document.getElementById("main-center"));

  const [isOpen, setOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [lockedCalendars, setLockedCalendars] = useState([]);
  const [allCalendars, setCalendars] = useState([]);
  const [toastMessage, setToastMessage] = useState("");
  const [savedWithChanges, setSavedWithChanges] = useState(false);

  const [response, callAPI] = useStarfishAPI({ isNewAPI: true });

  const closeModal = useCallback(() => {
    // Reset state and close modal and dialog (if open)
    setSelectedRows([]);
    setLockedCalendars([]);
    setOpen(false);
    setIsConfirmationOpen(false);
    setSavedWithChanges(false);
  }, []);

  const isButtonDisabled = selectedRows.length === 0;

  const allRowsSelected = allCalendars.length > 0 && 
    selectedRows.length === allCalendars.length;

  const unlockCalendars = () => {
    if ( isButtonDisabled ) return;
    setLockedCalendars(lockedCalendars.filter(id => !selectedRows.includes(id)));
  }

  const lockCalendars = () => {
    if ( isButtonDisabled ) return;
    setLockedCalendars(uniq(lockedCalendars.concat(selectedRows)));
  };

  const onLoadCalendars = calendars => {
    setCalendars(calendars);
    setLockedCalendars(calendars
      .filter(({lockingStatus}) => lockingStatus === "LOCKED")
      .map(({id}) => id)
    );
  };

  const isSubmitButtonDisabled = response.pending || allCalendars.length === 0;

  // Map all retrieved calendars to their new status
  const getData = () => allCalendars.map(({id, name}) => ({
    id,
    name,
    lockingStatus: lockedCalendars.includes(id) ? "LOCKED" : "UNLOCKED"
  }));

  const hasChanged = data => {
    const originalData = allCalendars
      .map(({id, name, lockingStatus}) => ({id, name, lockingStatus}));

    return !isEqual(data, originalData);
  };

  const onSubmit = () => {
    if ( isSubmitButtonDisabled ) return;

    const data = getData();
    if ( hasChanged(data) ) setSavedWithChanges(true);

    callAPI(`../api/lockable-schedules`, {
      method: "PUT",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  };

  const onBeforeClose = () => {
    if ( hasChanged(getData()) ) setIsConfirmationOpen(true);
    else closeModal();
  };

  useEffect(() => {
    if ( response.success ) {
      setToastMessage(savedWithChanges 
        ? "Calendar access updated" 
        : "No changes were made."
      );
      closeModal();
    }
    else if ( response.error ) {
      setToastMessage("Unable to update calendar access. Try again.");
    }
  }, [response, closeModal, setToastMessage]);

  return (
    <>
      <StyledButton
        aria-label="Lock Calendars" 
        inverted
        onClick={() => setOpen(true)} 
        className={css['button']}>
        <Icon icon="lock" /> Calendars
      </StyledButton>
      <Modal
        isOpen={isOpen}
        onRequestClose={onBeforeClose}
        overlayClassName={css["overlay"]}
        className={css["modal"]}
        contentLabel="Calendar Administration Modal"
      >
        <div className={css["container"]}>
          <Header closeModal={onBeforeClose} />
          <div className={css["content"]}>
            <h1>Calendar Administration</h1>
            <h2>
              Locked calendars prevent changes outside of existing schedule blocks.
              Unlocked calendars have no limitations.
            </h2>
            <div className={css['table-container']}>
              <CalendarTable
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                lockedCalendars={lockedCalendars}
                onLoadCalendars={onLoadCalendars}
                setToastMessage={setToastMessage}
                allRowsSelected={allRowsSelected}
                allCalendars={allCalendars}
              />
              <div className={css['footer']}>
                <div className={css['selected']}>Selected: {selectedRows.length}</div>
                <div className={css['button-container']}>
                  <StyledButton
                    onClick={unlockCalendars} 
                    aria-disabled={isButtonDisabled}
                    inverted>
                    <Icon aria-hidden={true} icon="lock_open" />
                    Unlock
                  </StyledButton>
                  <StyledButton
                    onClick={lockCalendars}
                    aria-disabled={isButtonDisabled}
                    inverted>
                    <Icon aria-hidden={true} icon="lock" />
                    Lock
                  </StyledButton>
                </div>
              </div>
            </div>
          </div>
          <Footer
            primaryButton={
              <StyledButton 
                onClick={onSubmit} 
                aria-disabled={isSubmitButtonDisabled}>
                {response.pending ? "Saving..." : "Save"}
              </StyledButton>
            }
            secondaryButton={
              <StyledButton asLink onClick={onBeforeClose}>
                Cancel
              </StyledButton>
            }
          />
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={isConfirmationOpen}
        cancelText="Leave"
        confirmText={response.pending ? "Saving..." : "Save & Leave"}
        headerText="Do you want to save your changes before you leave?"
        descriptionText="Any unsaved changes to calendar access will be lost."
        onClose={() => {
          setIsConfirmationOpen(false);
          closeModal();
          setToastMessage("Your changes were discarded.");
        }}
        onConfirm={onSubmit}
      />
      {toastMessage && <Toast 
        message={toastMessage} 
        onTimeout={() => setToastMessage("")}   
      />}
    </>
  );
}

export default hot(module)(App);
